import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  FormControl,
  Heading,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Tag,
  Text,
} from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ContactPersonDto,
  ContactSubjectType,
  type EmailInviteDto,
  EmailInviteLanguage,
  MinimalOrgDto,
  type SourcingEventDto,
  useAddEmailInviteToPrivateSourcingEventMutation,
  useGetOrganizationEntryQuery,
  useGetSourcingEventQuery,
} from "../../autogen/bff-api";
import { ContactPersonForm } from "../../common/ContactPersonForm";
import { normalizeEmail } from "../../common/email/normalizeEmail";
import { useApiError } from "../../common/errors/useApiError";
import { ModalSkeleton } from "../../common/ModalSkeleton";
import { isDeadlineRefusal } from "./bidDeadlineWindow";
import { isApiErrorDto } from "../../common/errors/isApiErrorDto";
import { isFetchBaseQueryError } from "../../common/errors/isFetchBaseQueryError";
import { ListLoadError } from "../../common/ListLoadError";
import {
  RegisteredAccountHint,
  type RegisteredMember,
} from "../sourcing-events/buyer/wizard/steps/private/RegisteredAccountHint";

interface Props {
  eventId: string;
  minimalOrgEntry?: Pick<MinimalOrgDto, "id" | "name">;
  alreadyInvited: Pick<EmailInviteDto, "email" | "organizationEntry">[];
  onClose: () => void;
  onInvitingClosed?: () => void;
  onInviteRegisteredMember?: (member: RegisteredMember) => void | Promise<void>;
  showPublishedWarning?: boolean;
}

export const InviteOrganizationEntryModal = ({
  eventId,
  minimalOrgEntry,
  alreadyInvited,
  onClose,
  onInvitingClosed,
  onInviteRegisteredMember,
  showPublishedWarning = false,
}: Props) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const [addEmailInvite] = useAddEmailInviteToPrivateSourcingEventMutation();
  const { refetch: refetchEvent } = useGetSourcingEventQuery({ eventId });

  const { data: organizationEntry, isLoading, isError, isFetching, refetch } = useGetOrganizationEntryQuery(
    minimalOrgEntry ? { entryId: minimalOrgEntry.id } : skipToken,
  );

  const [selectedContactPersons, setSelectedContactPersons] = useState<ContactPersonDto[]>([]);
  const [invitedHere, setInvitedHere] = useState<string[]>([]);
  const [failedEmails, setFailedEmails] = useState<string[]>([]);
  const [companyInvitedFor, setCompanyInvitedFor] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [justTaken, setJustTaken] = useState<string[]>([]);
  const submitting = useRef(false);

  const invitesOf = (email: string) =>
    alreadyInvited.filter((invite) => normalizeEmail(invite.email) === normalizeEmail(email));
  const invitedForThisCompany = (email: string) =>
    invitedHere.includes(normalizeEmail(email)) ||
    invitesOf(email).some((invite) => invite.organizationEntry?.id === minimalOrgEntry?.id);
  const invitedForAnotherCompany = (email: string) =>
    invitedForThisCompany(email)
      ? undefined
      : invitesOf(email).find((invite) => invite.organizationEntry?.id !== minimalOrgEntry?.id);
  const companyInvitedInstead = (email: string): string | undefined => companyInvitedFor[normalizeEmail(email)];
  const invitedTag = (email: string) => {
    const other = invitedForAnotherCompany(email);
    if (!other) return t("Invited");
    const company = other.organizationEntry?.name;
    return company ? t("Invited for {{company}}", { company }) : t("Already invited in this procurement");
  };
  const isInvited = (email: string) =>
    invitedForThisCompany(email) ||
    invitedForAnotherCompany(email) !== undefined ||
    companyInvitedInstead(email) !== undefined;
  const hasFailed = (email: string) => failedEmails.includes(normalizeEmail(email));
  const isSelected = (email: string) =>
    selectedContactPersons.some((selected) => normalizeEmail(selected.email) === normalizeEmail(email));
  const pendingContactPersons = selectedContactPersons.filter((person) => !isInvited(person.email));

  const handleClose = () => {
    setSelectedContactPersons([]);
    setInvitedHere([]);
    setFailedEmails([]);
    setCompanyInvitedFor({});
    setJustTaken([]);
    onClose();
  };

  const toggle = (person: ContactPersonDto, checked: boolean) => {
    const email = normalizeEmail(person.email);
    setSelectedContactPersons((prev) => {
      const others = prev.filter((selected) => normalizeEmail(selected.email) !== email);
      return checked ? [...others, person] : others;
    });
    if (!checked) setFailedEmails((prev) => prev.filter((failed) => failed !== email));
  };

  const markInvited = (email: string) => {
    setInvitedHere((prev) => [...prev, email]);
    setFailedEmails((prev) => prev.filter((failed) => failed !== email));
    setSelectedContactPersons((prev) => prev.filter((selected) => normalizeEmail(selected.email) !== email));
  };

  const createInvites = async () => {
    if (!minimalOrgEntry) throw new Error("Organization entry was unexpectedly undefined");
    if (submitting.current) return;
    submitting.current = true;
    setIsSubmitting(true);
    let lastError: unknown;
    let anyInvited = false;
    const contested: string[] = [];
    setJustTaken([]);
    try {
      for (const person of inListedOrder(pendingContactPersons, organizationEntry?.contactPersons ?? [])) {
        const email = normalizeEmail(person.email);
        try {
          await addEmailInvite({
            eventId,
            addEmailInviteRequest: {
              email: person.email,
              language: person.language ?? EmailInviteLanguage.No,
              organizationEntryId: minimalOrgEntry.id,
            },
          }).unwrap();
          anyInvited = true;
          markInvited(email);
        } catch (e) {
          if (isAlreadyInvitedRefusal(e)) {
            contested.push(email);
            continue;
          }
          if (isDeadlineRefusal(e)) {
            trigger(e);
            onInvitingClosed?.();
            handleClose();
            return;
          }
          lastError = e;
          setFailedEmails((prev) => (prev.includes(email) ? prev : [...prev, email]));
        }
      }
      const taken = contested.length > 0 ? await takenByAnotherInvite(contested, minimalOrgEntry.id) : [];
      if (taken.length > 0) {
        setJustTaken(taken);
        setSelectedContactPersons((prev) => prev.filter((selected) => !taken.includes(normalizeEmail(selected.email))));
        if (lastError !== undefined) trigger(lastError);
      } else if (lastError === undefined) handleClose();
      else trigger(lastError);
    } finally {
      if (anyInvited) await fetchAfterEverySave(refetchEvent);
      submitting.current = false;
      setIsSubmitting(false);
    }
  };

  const takenByAnotherInvite = async (emails: string[], organizationEntryId: string): Promise<string[]> => {
    await refetchEvent();
    const invites = emailInvitesOfEvent((await refetchEvent())?.data);
    return emails.filter((email) => {
      const forThisCompany = invites.some(
        (invite) => normalizeEmail(invite.email) === email && invite.organizationEntry?.id === organizationEntryId,
      );
      if (forThisCompany) markInvited(email);
      return !forThisCompany;
    });
  };

  const takenForAnotherCompany = justTaken.filter(
    (email) => !invitesOf(email).some((invite) => invite.organizationEntry?.id === minimalOrgEntry?.id),
  );
  const refusedButInvitedHere =
    justTaken.length > 0 &&
    takenForAnotherCompany.length === 0 &&
    pendingContactPersons.length === 0 &&
    failedEmails.length === 0 &&
    !isSubmitting;

  useEffect(() => {
    if (refusedButInvitedHere) handleClose();
  });

  return (
    <Modal isOpen={!!minimalOrgEntry} onClose={handleClose} size="3xl">
      <ModalOverlay />
      <ModalContent width="full" height="60%" minHeight="60%" maxHeight="80%" padding="5" overflow="hidden">
        <ModalCloseButton />
        <Flex minHeight="full" height="full" flexDirection="column" overflow="hidden">
          <Heading as="h2" size="md" pb="2" pr="5">
            {t("Select persons to invite for")} {minimalOrgEntry?.name}
          </Heading>
          {takenForAnotherCompany.length > 0 && (
            <Alert status="error" rounded="md" mb="2">
              <AlertIcon />
              {t("{{emails}} has just been invited in this procurement, so it can't be invited here as well.", {
                emails: takenForAnotherCompany.join(", "),
              })}
            </Alert>
          )}
          {isLoading && <ModalSkeleton />}
          {isError && !organizationEntry && (
            <ListLoadError message={t("Could not load contact persons")} onRetry={refetch} isRetrying={isFetching} />
          )}
          {organizationEntry && (
            <Flex flexDirection="column" grow="1" justifyContent="space-between" overflowY="auto">
              <Flex flexDirection="column">
                <Box pt="5" pl="2">
                  <CheckboxGroup colorScheme="teal">
                    {organizationEntry.contactPersons?.map((p) => (
                      <Box key={p.email} pb="2">
                        <Checkbox
                          width="full"
                          isDisabled={isInvited(p.email)}
                          isChecked={isInvited(p.email) || isSelected(p.email)}
                          onChange={(e) => toggle(p, e.target.checked)}
                        >
                          {`${p.name} (${p.email})`}
                          {isInvited(p.email) && (
                            <Tag size="sm" ml="2">
                              {invitedTag(p.email)}
                            </Tag>
                          )}
                          {!isInvited(p.email) && hasFailed(p.email) && (
                            <Tag size="sm" colorScheme="red" ml="2">
                              {t("Invitation failed")}
                            </Tag>
                          )}
                        </Checkbox>
                        {!isInvited(p.email) && hasFailed(p.email) && (
                          <Text fontSize="sm" color="smError" pl="6">
                            {t("This contact could not be invited. Untick or correct the contact and try again.")}
                          </Text>
                        )}
                        {companyInvitedInstead(p.email) !== undefined && (
                          <Text fontSize="sm" color="smMuted" pl="6">
                            {t("{{company}} is invited, with {{person}} as recipient.", {
                              company: companyInvitedInstead(p.email),
                              person: p.name,
                            })}
                          </Text>
                        )}
                        {!isInvited(p.email) && onInviteRegisteredMember && (
                          <Box pl="6">
                            <RegisteredAccountHint
                              eventId={eventId}
                              email={p.email}
                              canInvite={() => true}
                              sendsAtOnce={showPublishedWarning}
                              onInvite={async (member) => {
                                await onInviteRegisteredMember(member);
                                toggle(p, false);
                                setCompanyInvitedFor((invited) => ({
                                  ...invited,
                                  [normalizeEmail(p.email)]: member.organization.name,
                                }));
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    ))}
                  </CheckboxGroup>
                </Box>
                <ContactPersonForm
                  subject={{ subjectType: ContactSubjectType.OrganizationEntry, subjectId: organizationEntry.id }}
                  onSubmit={(newContactPerson) =>
                    setSelectedContactPersons((prev) => [...prev, newContactPerson])
                  }
                />
              </Flex>
              <FormControl width="full" pt="5">
                <Button isDisabled={!pendingContactPersons.length} isLoading={isSubmitting} onClick={createInvites}>
                  {t("Create invite")}
                </Button>
              </FormControl>
            </Flex>
          )}
        </Flex>
      </ModalContent>
    </Modal>
  );
};

const inListedOrder = (selected: ContactPersonDto[], listed: ContactPersonDto[]): ContactPersonDto[] => {
  const position = (person: ContactPersonDto) => {
    const index = listed.findIndex((candidate) => normalizeEmail(candidate.email) === normalizeEmail(person.email));
    return index === -1 ? listed.length : index;
  };
  return [...selected].sort((a, b) => position(a) - position(b));
};

const emailInvitesOfEvent = (event: SourcingEventDto | undefined): Pick<EmailInviteDto, "email" | "organizationEntry">[] => {
  const context = event?.eventContext as { emailInvites?: EmailInviteDto[] } | undefined;
  return context?.emailInvites ?? [];
};

const isAlreadyInvitedRefusal = (e: unknown): boolean =>
  isFetchBaseQueryError(e) &&
  isApiErrorDto(e.data) &&
  e.data.errorCode === "zz_SourcingEventApiErrorCode_EmailInviteToAddAlreadyExists";

const fetchAfterEverySave = async (refetch: () => PromiseLike<unknown>) => {
  await refetch();
  await refetch();
};
